import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { DataView } from "primereact/dataview";
import { Dropdown } from "primereact/dropdown";
import { Rating } from "primereact/rating";
import { Tag } from "primereact/tag";
import { useSearchParams } from "react-router-dom";
import { classNames } from "primereact/utils";
import {
  getProducts,
  getCategories,
  getAllImages,
  getImageUrlFromCloudinary,
} from "../../services/productsService";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import "./Shop.css";

export default function Shop() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState({});
  const [first, setFirst] = useState(0);
  const [stock, setStock] = useState("");
  const [stockOptions, setStockOptions] = useState([]);
  const [sortKey, setSortKey] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [sortField, setSortField] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [serie, setSerie] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [productImages, setProductImages] = useState({});
  const [rawImages, setRawImages] = useState([]);
  const [isPaymentEnabled, setIsPaymentEnabled] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false); // acordeón mobile
  const navigate = useNavigate();

  const itemsPerPageList = [
    { label: "12 por página", value: 12 },
    { label: "24 por página", value: 24 },
    { label: "48 por página", value: 48 },
  ];

  const sortOptions = [
    { label: "Precio: Mayor a Menor", value: "!cost" },
    { label: "Precio: Menor a Mayor", value: "cost" },
    { label: "Recién Actualizados", value: "!updated_at" },
    { label: "Novedades", value: "!created_at" },
  ];

  const estados = {
    PREORDER: "Pre-venta",
    INSTOCK: "Disponible",
    LOWSTOCK: "Ultimas Piezas",
    BACKORDER: "Bajo pedido",
    OUTOFSTOCK: "Agotado",
  };

  const dynamicStatusOptions = [
    { label: "Todo", value: "all" },
    ...stockOptions.map((cat) => ({
      label: estados[cat.status] || cat,
      value: cat.status || cat,
    })),
  ];

  const dynamicCategoryOptions = [
    { label: "Todas las categorías", value: "all" },
    ...categories.map((cat) => ({
      label: cat.category_name || cat,
      value: cat.category_name || cat,
    })),
  ];

  const dynamicBrandOptions = [
    { label: "Todos los marcas", value: "all" },
    ...brands.map((cat) => ({
      label: cat.brand || cat,
      value: cat.brand || cat,
    })),
  ];

  const dynamicSerieOptions = [
    { label: "Todas las series", value: "all" },
    ...series.map((cat) => ({
      label: cat.serie || cat,
      value: cat.serie || cat,
    })),
  ];

  /* ── Effects: misma lógica original ─────────────────────── */
  useEffect(() => {
    setFirst(0);
  }, [itemsPerPage]);

  useEffect(() => {
    //Limpiar filtros
    setNameFilter("");
    setStock("");
    setCategory("all");
    setBrand("all");
    setSerie("all");

    const nameFromUrl = searchParams.get("name") || "";
    const brandFromUrl = searchParams.get("brand") || "";
    const serieFromUrl = searchParams.get("serie") || "";
    const categoryFromUrl = searchParams.get("category") || "";
    const isNoveltyFromUrl = searchParams.get("novelty") || "";
    const preorderFromUrl = searchParams.get("preorder") || "";
    const salesFromUrl = searchParams.get("sales") || "";
    setNameSearch(nameFromUrl);
    getProducts({
      name: nameFromUrl,
      brand: brandFromUrl,
      serie: serieFromUrl,
      category: categoryFromUrl,
      novelty: isNoveltyFromUrl,
      preorder: preorderFromUrl,
      sales: salesFromUrl,
    })
      .then((data) => {
        const fetchedProducts = data.result || data;
        setProducts(fetchedProducts);

        // Derive unique categories from products
        const uniqueCategories = [
          ...new Set(fetchedProducts.map((p) => p.category).filter(Boolean)),
        ];
        //Derive unique brands from products
        const uniqueBrands = [
          ...new Set(fetchedProducts.map((p) => p.brand).filter(Boolean)),
        ];
        //Derive unique series from products
        const uniqueSeries = [
          ...new Set(fetchedProducts.map((p) => p.serie).filter(Boolean)),
        ];
        const uniqueStatus = [
          ...new Set(fetchedProducts.map((p) => p.status).filter(Boolean)),
        ];
        setBrands(uniqueBrands.map((brand) => ({ brand })));
        setSeries(uniqueSeries.map((serie) => ({ serie })));
        setCategories(uniqueCategories.map((cat) => ({ category_name: cat })));
        setStockOptions(uniqueStatus.map((status) => ({ status })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [searchParams]);

  useEffect(() => {
    getAllImages()
      .then((data) => setRawImages(data.result || data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!rawImages || rawImages.length === 0) return;
    let mounted = true;
    (async () => {
      for (const img of rawImages) {
        try {
          const ids = JSON.parse(img.image_id);
          if (!ids || ids.length === 0) continue;
          const productId = img.product_id;
          setImageLoading((prev) => ({ ...prev, [productId]: true }));
          const [imageUrl] = await getImageUrlFromCloudinary(img.folder_name, [
            ids[0],
          ]);
          if (!mounted) return;
          setProductImages((prev) => ({ ...prev, [productId]: imageUrl }));
        } catch (err) {
          console.error("Error loading image", img, err);
        } finally {
          if (mounted)
            setImageLoading((prev) => ({ ...prev, [img.product_id]: false }));
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [rawImages]);

  /* ── Helpers ─────────────────────────────────────────────── */
  const getSeverity = (product) => {
    const map = {
      PREORDER: "info",
      INSTOCK: "success",
      LOWSTOCK: "warning",
      BACKORDER: "help",
      OUTOFSTOCK: "danger",
    };
    return map[product.status] || null;
  };

  const onSortChange = (event) => {
    const value = event.value;
    if (value.indexOf("!") === 0) {
      setSortOrder(-1);
      setSortField(value.substring(1));
      setSortKey(value);
    } else {
      setSortOrder(1);
      setSortField(value);
      setSortKey(value);
    }
  };

  /* ── Productos filtrados (debe ir ANTES del sidebar) ────────── */
  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(nameFilter.toLowerCase()) &&
      (category === "all" ||
        category === "" ||
        product.category?.toLowerCase() === category.toLowerCase()) &&
      (brand === "all" ||
        brand === "" ||
        product.brand?.toLowerCase() === brand.toLowerCase()) &&
      (serie === "all" ||
        serie === "" ||
        product.serie?.toLowerCase() === serie.toLowerCase()) &&
      (stock === "all" ||
        stock === "" ||
        product.status?.toLowerCase() === stock.toLowerCase()),
  );
  /* ── Filtros (sidebar izquierdo) ─────────────────────────── */
  const sidebarFilters = (
    <aside className="shop-sidebar">
      {/* Búsqueda por nombre — siempre visible */}
      <div className="shop-sidebar__block">
        <p className="shop-sidebar__label">Buscar por nombre</p>
        <input
          type="text"
          className="shop-sidebar__input"
          placeholder="Nombre del producto..."
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
      </div>

      {/* Botón "Más filtros" — solo visible en mobile */}
      <button
        className={`shop-sidebar__toggle ${filtersOpen ? "shop-sidebar__toggle--open" : ""}`}
        onClick={() => setFiltersOpen((v) => !v)}
        aria-expanded={filtersOpen}
      >
        <i className={`pi ${filtersOpen ? "pi-minus" : "pi-sliders-h"}`} />
        {filtersOpen ? "Ocultar filtros" : "Más filtros"}
        <i className={`pi pi-chevron-down shop-sidebar__toggle-caret ${filtersOpen ? "shop-sidebar__toggle-caret--open" : ""}`} />
      </button>

      {/* Filtros secundarios — siempre visibles en desktop, acordeón en mobile */}
      <div className={`shop-sidebar__extra ${filtersOpen ? "shop-sidebar__extra--open" : ""}`}>

        {dynamicStatusOptions.length > 2 && (
          <div className="shop-sidebar__block">
            <p className="shop-sidebar__label">Stock</p>
            <Dropdown
              options={dynamicStatusOptions}
              value={stock}
              optionLabel="label"
              placeholder="Todos los productos"
              onChange={(e) => setStock(e.value)}
              className="shop-dropdown"
            />
          </div>
        )}

        {dynamicCategoryOptions.length > 2 && (
          <div className="shop-sidebar__block">
            <p className="shop-sidebar__label">Categoría</p>
            <Dropdown
              options={dynamicCategoryOptions}
              value={category}
              optionLabel="label"
              placeholder="Todas las categorías"
              onChange={(e) => setCategory(e.value)}
              className="shop-dropdown"
            />
          </div>
        )}

        {dynamicBrandOptions.length > 2 && (
          <div className="shop-sidebar__block">
            <p className="shop-sidebar__label">Marca</p>
            <Dropdown
              options={dynamicBrandOptions}
              value={brand}
              optionLabel="label"
              placeholder="Todos los productos"
              onChange={(e) => setBrand(e.value)}
              className="shop-dropdown"
            />
          </div>
        )}

        <div className="shop-sidebar__block">
          <p className="shop-sidebar__label">Series</p>
          <Dropdown
            options={dynamicSerieOptions}
            value={serie}
            optionLabel="label"
            placeholder="Todas las series"
            onChange={(e) => setSerie(e.value)}
            className="shop-dropdown"
          />
        </div>

        <Button
          label="Limpiar filtros"
          className="p-button-outlined"
          onClick={() => {
            setNameFilter("");
            setStock("");
            setCategory("all");
            setBrand("all");
            setSerie("all");
          }}
        />
      </div>

      <div className="shop-sidebar__count">
        <strong>{filteredProducts.length}</strong> productos encontrados
      </div>
    </aside>
  );

  /* ── Toolbar (encima del DataView) ───────────────────────── */
  const toolbar = (
    <div className="shop-toolbar">
      <Dropdown
        options={sortOptions}
        value={sortKey}
        optionLabel="label"
        placeholder="Ordenar por..."
        onChange={onSortChange}
        className="shop-dropdown shop-dropdown--sm"
      />
      <div className="shop-toolbar__spacer" />
      <span className="shop-toolbar__label">Mostrar</span>
      <Dropdown
        options={itemsPerPageList}
        value={itemsPerPage}
        optionLabel="label"
        placeholder="12 por página"
        onChange={(e) => setItemsPerPage(e.value)}
        className="shop-dropdown shop-dropdown--sm"
      />
    </div>
  );

  /* ── Tarjeta de producto (grid) ─────────────────────────────── */
  const itemTemplate = (product) => {
    const imageUrl =
      productImages[product.id] ||
      `https://primefaces.org/cdn/primereact/images/product/${product.image}`;
    const isImgLoading = imageLoading[product.id];
    const discount = Number(product.sale_discount || 0);
    const hasDiscount = discount > 0;
    const finalPrice = hasDiscount
      ? product.cost - (product.cost * discount) / 100
      : product.cost;

    const productDate = new Date(product.created_at);
    const isRecentlyAdded = productDate > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

    return (
      <div
        className="shop-card"
        key={product.id}
        onClick={() => navigate(`/product/${product.id}`)}
      >
        {/* Imagen */}
        <div className="shop-card__img-wrap">
          {hasDiscount && (
            <>
              <span className="shop-card__discount-badge">
                <i className="pi pi-tag" /> -{discount}%
              </span>
            </>
          )}

          {isRecentlyAdded && (
            <span className="shop-card__new-badge">
              <i className="pi pi-star" />
            </span>
          )}
          <Tag
            value={estados[product.status] || product.status}
            severity={getSeverity(product)}
            className="shop-card__status-badge"
          />
          {isImgLoading ? (
            <ProgressSpinner style={{ width: "36px", height: "36px" }} />
          ) : (
            <img src={imageUrl} alt={product.name} loading="lazy" />
          )}
        </div>

        {/* Info */}
        <div className="shop-card__body">
          {product.brand && (
            <span className="shop-card__brand">{product.brand}</span>
          )}
          <p className="shop-card__name">{product.name}</p>
          {product.serie && (
            <span className="shop-card__serie">{product.serie}</span>
          )}

          <div className="shop-card__footer">
            <div className="shop-card__price-wrap">
              {hasDiscount && (
                <span className="shop-card__old-price">${product.cost}</span>
              )}
              <span className={`shop-card__price${hasDiscount ? " shop-card__price--discount" : ""}`}>
                ${hasDiscount ? finalPrice.toFixed(2) : product.cost}
              </span>
            </div>
            <a
              href="https://wa.me/message/CJH5F4NQS6PNN1"
              target="_blank"
              rel="noopener noreferrer"
              className="shop-card__wa-btn"
              aria-label="Contactar por WhatsApp"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="pi pi-whatsapp" />
            </a>
          </div>
        </div>
      </div>
    );
  };

  const listTemplate = (items) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="shop-grid">
        {items.map((product) => itemTemplate(product))}
      </div>
    );
  };

  const emptyTemplate = () => (
    <div className="shop-empty">
      <i className="pi pi-inbox shop-empty__icon" />
      <p className="shop-empty__text">No se encontraron productos</p>
      <p className="shop-empty__sub">
        Prueba ajustando los filtros de búsqueda
      </p>
    </div>
  );

  /* ── Loading state ───────────────────────────────────────── */
  const loadingState = (
    <div className="shop-loading">
      <ProgressSpinner
        style={{ width: "48px", height: "48px" }}
        aria-label="Cargando productos"
      />
      <p className="shop-loading__title">Cargando productos...</p>
      <p className="shop-loading__sub">
        Los servicios se están encendiendo. Puede tardar hasta un minuto —
        disculpa la espera.
      </p>
    </div>
  );

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div className="shop-page">
      {sidebarFilters}

      <div className="shop-main">
        {loading ? (
          loadingState
        ) : (
          <>
            {/* Banner de resultados de búsqueda */}
            {nameSearch && (
              <div className="shop-search-banner">
                <i className="pi pi-search shop-search-banner__icon" />
                <div>
                  <span className="shop-search-banner__label">Resultados de búsqueda</span>
                  <span className="shop-search-banner__query">"{nameSearch}"</span>
                </div>
                <span className="shop-search-banner__count">
                  {filteredProducts.length} {filteredProducts.length === 1 ? "producto" : "productos"}
                </span>
              </div>
            )}
            {/*Banner de pagos desactivados*/}
            {!isPaymentEnabled && (
              <div className="shop-payment-banner">
                <i className="pi pi-credit-card shop-payment-banner__icon" />
                <div>
                  <span className="shop-payment-banner__label">Los pagos en línea estan siguen en desarollo, disculpa las molestias no dudes en contactarnos si te interesa un producto.
                    {" "}
                    <i className="pi pi-whatsapp pd-contact-shop__icon" />
                    {" "}
                    <a href="https://wa.me/message/CJH5F4NQS6PNN1" target="_blank" rel="noopener noreferrer" className="pd-contact-shop__link">Whatsapp</a> 
                    </span>
                </div>
              </div>
            )}
            <DataView
              className="shop-dataview"
              value={filteredProducts}
              listTemplate={listTemplate}
              header={toolbar}
              paginator
              rows={itemsPerPage}
              first={first}
              onPage={(e) => setFirst(e.first)}
              sortField={sortField}
              sortOrder={sortOrder}
              emptyMessage={emptyTemplate()}
              paginatorClassName="shop-paginator"
            />
          </>
        )}
      </div>
    </div>
  );
}
